// API Service for Jury Portal
import { API_URL } from '../config/api';
import { ApiResponse, AuthResponse, Assignment, Score } from '../types';

// Token management
const getToken = (): string | null => {
  return localStorage.getItem('jury_token');
};

const getAuthHeaders = (): HeadersInit => {
  const token = getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    (headers as any)['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

// Handle API responses
const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('jury_token');
      localStorage.removeItem('jury');
      window.location.href = '/login';
      throw new Error('Authentication failed');
    }

    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch (e) {
      // Ignore parse error
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

// API Service Class
class ApiService {
  static async get<T = any>(url: string): Promise<T> {
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return handleResponse<T>(response);
  }

  static async post<T = any>(url: string, data?: any): Promise<T> {
    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });
    return handleResponse<T>(response);
  }

  static async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await fetch(url, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });
    return handleResponse<T>(response);
  }

  static async postPublic<T = any>(url: string, data?: any): Promise<T> {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: data ? JSON.stringify(data) : undefined,
    });
    return handleResponse<T>(response);
  }
}

// Auth API
export const AuthAPI = {
  login: (credentials: { username: string; password: string }): Promise<AuthResponse> =>
    ApiService.postPublic(API_URL.AUTH.LOGIN, credentials),

  logout: (): Promise<ApiResponse> =>
    ApiService.post(API_URL.AUTH.LOGOUT),

  me: (): Promise<ApiResponse> =>
    ApiService.get(API_URL.AUTH.ME),

  changePassword: (data: { current_password: string; new_password: string; new_password_confirmation: string }): Promise<ApiResponse> =>
    ApiService.post(API_URL.AUTH.CHANGE_PASSWORD, data),
};

// Assignments API
export const AssignmentsAPI = {
  getList: (round?: number, status?: string): Promise<ApiResponse<Assignment[]>> => {
    let url = API_URL.ASSIGNMENTS.LIST;
    const params = new URLSearchParams();
    if (round) params.append('round', round.toString());
    if (status) params.append('status', status);
    if (params.toString()) url += `?${params.toString()}`;
    return ApiService.get(url);
  },

  getDetail: (id: number, bypassPhaseCheck: boolean = true): Promise<ApiResponse<Assignment>> =>
    ApiService.get(`${API_URL.ASSIGNMENTS.DETAIL}/${id}${bypassPhaseCheck ? '?bypass_phase_check=true' : ''}`),

  startScoring: (id: number): Promise<ApiResponse> =>
    ApiService.post(`${API_URL.ASSIGNMENTS.START_SCORING}/${id}/start`),

  submitScore: (id: number, data: { scores: Score[]; comment?: string }): Promise<ApiResponse> =>
    ApiService.post(`${API_URL.ASSIGNMENTS.SUBMIT_SCORE}/${id}/score`, data),
};

// Files API
export const FilesAPI = {
  getDownloadUrl: (submissionId: number, fileType: string): string =>
    `${API_URL.FILES.DOWNLOAD}/${submissionId}/${fileType}?token=${getToken()}`,
};

export { ApiService };
export default ApiService;
